import React, { useEffect, useState, useCallback, useMemo } from 'react';
import HeaderWithIcon from '../../components/common/HeaderWithIcon';
import ViewDetailsCard from './ViewDetailsCard';
import dice from '../../assets/icons/dices.svg?react';
import { useParams } from 'react-router-dom';
import { gamesApi } from '../../api/games';
import { useAuth } from '../../context/AuthContext';
import { ChevronLeft, ChevronRight, Search, ArrowUpDown } from 'lucide-react';
import dayjs from 'dayjs';

export default function ViewDetails() {
  const { tokens } = useAuth();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [gameResults, setGameResults] = useState([]);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('All');
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('none'); // 'none', 'win-high', 'win-low'

  useEffect(() => {
    if (tokens?.access?.token && id) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [tokens?.access?.token, id]);

  useEffect(() => {
    fetchData();
  }, [page]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch data when debounced search term changes
  useEffect(() => {
    if (tokens?.access?.token && id) {
      setPage(1); // Reset to first page when searching
      setSortBy('none'); // Reset sort when searching
      fetchData();
    }
  }, [debouncedSearchTerm]);

  // Fetch data when sort changes
  useEffect(() => {
    if (tokens?.access?.token && id) {
      setPage(1); // Reset to first page when sorting
      fetchData();
    }
  }, [sortBy]);

  const totalPageCount = React.useMemo(() => {
    return Math.ceil(totalPages / limit);
  }, [totalPages, limit]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Use search endpoint if there's a search term, otherwise use regular endpoint
      const response = debouncedSearchTerm.trim() 
        ? await gamesApi.searchGameResultsById(
            tokens.access.token,
            id,
            page,
            limit,
            debouncedSearchTerm,
            sortBy
          )
        : await gamesApi.fetchGameResultsById(
            tokens.access.token,
            id,
            page,
            limit,
            sortBy
          );

      if (response.status === 200 && response.data && response.data.rows) {
        console.log('API data successfully fetched:', response.data);
        setTotalPages(response.data.count);

        // Transform API data
        const transformedData = transformApiData(response.data);
        setGameResults(transformedData);
      } else {
        // Fallback to sample data if API returns empty results
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data');
      // Fallback to sample data if API fails
    } finally {
      setLoading(false);
    }
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  // Transform API data to the format we need
  const transformApiData = (apiData) => {
    if (
      !apiData.rows ||
      !Array.isArray(apiData.rows) ||
      apiData.rows.length === 0
    ) {
      return [];
    }

    // Group data by role
    const groupedByRole = {
      All: [],
      Players: [],
      Agents: [],
      Coordinators: [],
    };

    apiData.rows.forEach((item) => {
      const roleName = getRoleName(item.user?.roleId);
      const isFloatingWin = item.user?.id !== item.bets?.User?.id;
      let floatingWinSource = undefined;
      let floatingWinSourceRole = undefined;
      if (isFloatingWin) {
        floatingWinSource = item.bets?.User?.name;
        floatingWinSourceRole = getRoleName(item.bets?.User?.roleId);
      }
      const gameCard = {
        game: item.user?.name || 'Unknown',
        role: roleName,
        numbers: formatNumbers(item.bets?.combination),
        drawDate: item.bets?.createdAt
          ? dayjs(item.bets.createdAt).format('ddd, MMM D, YYYY')
          : 'N/A',
        drawTime: item.bets?.drawTime || 'N/A',
        amount: parseFloat(item.bets?.amount) || 0,
        totalHits: item.bets?.bettingOption || 'N/A',
        totalWin: item.win || 0,
        ticketNumber: item.bets?.transactionReceiptsId || 'N/A',
        isFloatingWin,
        floatingWinSource,
        floatingWinSourceRole,
      };

      // Add to All group
      groupedByRole.All.push(gameCard);

      // Add to specific role group
      if (roleName === 'Coordinator') {
        groupedByRole.Coordinators.push(gameCard);
      } else if (roleName === 'Agent') {
        groupedByRole.Agents.push(gameCard);
      } else if (roleName === 'Player') {
        groupedByRole.Players.push(gameCard);
      }
    });

    // Convert grouped data to the format needed for tabs
    return Object.entries(groupedByRole)
      .filter(([_, cards]) => cards.length > 0)
      .map(([title, cards]) => ({
        title,
        cards,
      }));
  };

  // Function to get role name from role ID
  const getRoleName = (roleId) => {
    const roleMap = {
      1: 'Admin',
      2: 'Incorporator',
      3: 'Sub Operator',
      4: 'General Coordinator',
      5: 'Coordinator',
      6: 'Sub Coordinator',
      7: 'Agent',
      8: 'Sub Agent',
      9: 'Player',
    };
    return roleMap[roleId] || 'Player';
  };

  // Format numbers for display
  const formatNumbers = (combination) => {
    if (!combination) return [];

    // Simply convert each digit to a number object
    return combination.split('').map((digit) => ({
      num: parseInt(digit),
    }));
  };

  // Set sample data as fallback
  const setSampleData = () => {
    const simpleData = [
      {
        title: 'All',
        cards: [
          {
            game: 'Sheen Sheen',
            role: 'Agent',
            numbers: [{ num: 6 }, { num: 0 }, { num: 5 }],
            drawDate: 'Wed, Aug 28, 2024',
            drawTime: '2:00 PM',
            amount: 5.0,
            totalHits: 'Ramble',
            totalWin: 583.33,
            ticketNumber: '16f5008f-03d9-4a35-a56d-75afcc33426b',
          },
        ],
      },
      {
        title: 'Players',
        cards: [
          {
            game: 'Ronnie Acosta',
            role: 'Player',
            numbers: [{ num: 6 }, { num: 0 }, { num: 5 }],
            drawDate: 'Wed, Aug 28, 2024',
            drawTime: '2:00 PM',
            amount: 10.0,
            totalHits: 'Ramble',
            totalWin: 1166.0,
            ticketNumber: 'a56f13e3-e8c6-45c8-864b-f0f845de09c1',
          },
        ],
      },
      {
        title: 'Agents',
        cards: [
          {
            game: 'Sheen Sheen',
            role: 'Agent',
            numbers: [{ num: 6 }, { num: 0 }, { num: 5 }],
            drawDate: 'Wed, Aug 28, 2024',
            drawTime: '2:00 PM',
            amount: 5.0,
            totalHits: 'Ramble',
            totalWin: 583.33,
            ticketNumber: '16f5008f-03d9-4a35-a56d-75afcc33426b',
          },
        ],
      },
    ];
    console.log('Setting fallback sample data:', simpleData);
    setGameResults(simpleData);
  };

  // Get the active tab data (sorting is now handled by backend)
  const getActiveTabData = () => {
    const activeTabData = gameResults.find((tab) => tab.title === activeTab);
    return activeTabData?.cards || [];
  };

  return (
    <div className='min-h-screen relative overflow-hidden font-poppins'>
      <HeaderWithIcon
        title='View Details'
        icon={dice}
        className='bg-[#0E64D1] text-white'
      />
      <div className='mt-4 px-4'>
        {loading ? (
          <div className='flex justify-center items-center h-[calc(100vh-100px)]'>
            <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0E64D1]'></div>
          </div>
        ) : error ? (
          <div className='flex justify-center items-center h-[calc(100vh-100px)] text-red-500'>
            {error}
          </div>
        ) : gameResults.length === 0 ? (
          <div className='flex justify-center items-center h-[calc(100vh-100px)] text-gray-500'>
            No results found
          </div>
        ) : (
          <div>
            {/* Search and Controls Section */}
            <div className='space-y-4'>
              {/* Search and Sort Row */}
              <div className='flex flex-col sm:flex-row gap-3 justify-center items-center'>
                {/* Search Input */}
                <div className='relative w-full max-w-sm'>
                  <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' size={16} />
                  <input
                    type='text'
                    placeholder='Search by name...'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className='w-full pl-10 pr-4 py-2.5 border border-[#E1E7EF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0E64D1] focus:border-transparent'
                  />
                </div>
                
                {/* Sort Dropdown */}
                <div className='relative w-full max-w-xs sm:max-w-[160px]'>
                  <ArrowUpDown className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' size={16} />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className='w-full pl-10 pr-8 py-2.5 border border-[#E1E7EF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0E64D1] focus:border-transparent bg-white appearance-none cursor-pointer'
                  >
                    <option value='none'>No Sort</option>
                    <option value='win-high'>Highest Win</option>
                    <option value='win-low'>Lowest Win</option>
                  </select>
                  {/* Custom dropdown arrow */}
                  <div className='absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none'>
                    <svg className='w-4 h-4 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Tab Buttons */}
              <div className='flex justify-center gap-1.5'>
                {gameResults.map((tab, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTab(tab.title)}
                    className={`py-2.5 px-3 rounded-md text-xs font-normal transition-all duration-300 ${
                      activeTab === tab.title
                        ? 'bg-[#65758B] text-white'
                        : 'bg-[#E1E7EF] text-[#65758B] hover:text-white'
                    }`}
                  >
                    {tab.title}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Display Cards for Active Tab */}
            <div className='mt-6'>
              {getActiveTabData().length > 0 ? (
                getActiveTabData().map((card, idx) => (
                  <div key={idx} className='mb-4'>
                    <ViewDetailsCard {...card} />
                  </div>
                ))
              ) : (
                <div className='text-center text-gray-500 py-4'>
                  No cards available in this tab
                </div>
              )}
            </div>
            
            {/* Pagination */}
            <div className='flex justify-between items-center mt-4 mb-4'>
              <button
                onClick={handlePrevPage}
                disabled={page === 1}
                className={`flex items-center space-x-1 px-3 py-2 rounded-lg ${
                  page === 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-blue-600'
                }`}
              >
                <ChevronLeft size={16} />
                <span>Previous</span>
              </button>

              <span className='text-sm text-gray-700'>
                Page {page} of {totalPageCount}
              </span>

              <button
                onClick={handleNextPage}
                disabled={page === totalPageCount}
                className={`flex items-center space-x-1 px-3 py-2 rounded-lg ${
                  page === totalPageCount
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-blue-600'
                }`}
              >
                <span>Next</span>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
